/**
 * useAuth Hook
 * Provides authentication functionality with automatic error handling and loading states
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { loginStart, loginSuccess, loginFailure, signupStart, signupSuccess, signupFailure, logout as logoutAction } from '../store/authSlice';
import { UserLoginRequest, UserCreateInput, ApiError, UserLoginResponse, ApiResponse } from '@ocktra/shared-types';
import { apiClient } from '../services/api/apiClient';
import { addDataFeedProvider,addTradingProvider } from '../store/settingsSlice';
import { ProviderConnection } from '@ocktra/shared-types';
import { DataFeedProviderType, ProviderFactory,TradingProviderType } from '../services/providers';
import { wsClient } from '../services/websocket';

export const useAuth = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchConnections = async (): Promise<void> => {
        try {
            const response = await apiClient.post<ApiResponse<ProviderConnection[]>>('/provider/connections');

            console.log('Fetched provider connections on login:', response);

            // Separate connections by type
            const dataFeedProviders: ProviderConnection[] = [];
            const tradingProviders: ProviderConnection[] = [];

            const connections=response.data;
            if (!connections) {
                console.warn('No provider connections data received');
                return;
            }

            connections.forEach(connection => {
                if (connection.providerType === 'data_provider') {
                    dataFeedProviders.push(connection);
                    const provider = ProviderFactory.getDataFeedProvider(connection.providerName as DataFeedProviderType);
                    provider?.updateConnectionDetails(connection);
                } else if (connection.providerType === 'trading_provider') {
                    tradingProviders.push(connection);
                    const provider= ProviderFactory.getTradingProvider(connection.providerName as TradingProviderType);
                    provider?.updateConnectionDetails(connection);
                }
            });

            dataFeedProviders.forEach(provider => {
                if(provider.status==='connected'){
                    wsClient.sendMessage({
                        type:'provider_connect',
                        payload:{ connectionId:provider.id}
                    });
                }
                dispatch(addDataFeedProvider(provider));
            });

            tradingProviders.forEach(provider => {
                dispatch(addTradingProvider(provider));
            });
        } catch (err) {
            console.error('Failed to fetch provider connections:', err);
            // Don't fail login if provider fetch fails
        }
    }

    const login = useCallback(async (credentials: UserLoginRequest) => {
        setIsLoading(true);
        setError(null);
        dispatch(loginStart());

        try {
            const response = await apiClient.post<ApiResponse<UserLoginResponse>>('/login', credentials);
            console.log('Login response:', response);

            if(!response.data){
                throw new Error('No user data returned from login');
            }
            const data=response.data;
            // Set tokens on apiClient immediately so all subsequent requests have auth
            apiClient.setTokens(data.accessToken, data.refreshToken);

            dispatch(loginSuccess({
                userId: data.user.userId,
                email: data.user.email,
                isEmailVerified: data.user.isVerified,
                displayName: data.user.firstName + ' ' + data.user.lastName,
                accessToken: data.accessToken,
                refreshToken: data.refreshToken
            }));

            // Fetch provider connections after successful login
            await fetchConnections();

            return { success: true, user: data.user };
        } catch (err) {
            const apiError = err as ApiError;
            const errorMessage = apiError.message || 'Login failed. Please try again.';

            setError(errorMessage);
            dispatch(loginFailure(errorMessage));

            return { success: false, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    const signup = useCallback(async (credentials: UserCreateInput) => {
        setIsLoading(true);
        setError(null);
        dispatch(signupStart());

        try {
            const response = await apiClient.post<UserLoginResponse>('/signup', credentials);

            // Set tokens on apiClient immediately so all subsequent requests have auth
            apiClient.setTokens(response.accessToken, response.refreshToken);

            dispatch(signupSuccess({
                userId: response.user.userId,
                email: response.user.email,
                isEmailVerified: response.user.isVerified,
                displayName: response.user.firstName + ' ' + response.user.lastName,
                accessToken: response.accessToken,
                refreshToken: response.refreshToken
            }));

            // Fetch provider connections after successful signup
            await fetchConnections();

            return { success: true, user: response.user };
        } catch (err) {
            const apiError = err as ApiError;
            const errorMessage = apiError.message || 'Signup failed. Please try again.';

            setError(errorMessage);
            dispatch(signupFailure(errorMessage));

            return { success: false, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    const logout = useCallback(async () => {
        try {
            await apiClient.post('/logout');
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            apiClient.clearToken();
            dispatch(logoutAction());
            navigate('/login');
        }
    }, [dispatch, navigate]);

    const requestPasswordReset = useCallback(async (email: string) => {
        setIsLoading(true);
        setError(null);

        try {
            await apiClient.post('/forgot-password/request', { email });
            return { success: true };
        } catch (err) {
            const apiError = err as ApiError;
            const errorMessage = apiError.message || 'Failed to send reset email.';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, []);

    const verifyEmail = useCallback(async (token: string) => {
        setIsLoading(true);
        setError(null);

        try {
            await apiClient.post('/verify-email', { token });
            return { success: true };
        } catch (err) {
            const apiError = err as ApiError;
            const errorMessage = apiError.message || 'Email verification failed.';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        login,
        signup,
        logout,
        requestPasswordReset,
        verifyEmail,
        isLoading,
        error,
        setError
    };
};
